/**
 * Outlook action pills — which buttons appear under a reply, and what each does.
 *
 * See docs/outlook-action-pills-design.md. The short version:
 *
 *     buttons = OUTLOOK_BARS[skill][surface] − buttons this persona may not use
 *
 * No part of that involves the model. Both inputs are facts we already hold by
 * the time the turn ends: the backend tells us which skill answered (`skill_key`
 * on the `done` event), and Outlook tells us which surface is open.
 *
 * ## Why two tables
 *
 * `OUTLOOK_PILL_SPECS` says what each button IS — its label, what it does when
 * clicked, and which skill the persona needs for it to be allowed at all.
 * `OUTLOOK_BARS` says WHICH buttons apply to a given skill and surface.
 *
 * They are split because buttons repeat: Copy and Book meeting are on every bar.
 * Merged into one table, their label/handler/gating would be written three times
 * and drift. Splitting also keeps the persona out of the tables entirely — it is
 * applied at runtime as a filter between them, so 11 skills × 2 surfaces stays
 * 22 rows instead of multiplying by every possible persona.
 *
 * This mirrors `WORD_PILL_SPECS` in useChatHelpers.ts, whose comment makes the
 * same point: "labels/handlers come from WORD_PILL_SPECS, never from string
 * literals at the call sites."
 */

import { escapeHtml } from "../../../integrations/tools/outlook/html";
import { REPLY_TARGET_CONVERSATION_ARG } from "../../../integrations/tools/outlook/replyTarget";
import { getSkillCommandsForHost } from "../slashCommands/registry";
import type { Channel } from "../../shared/host";
import type { EmailDraft } from "../../integrations/email/types";
import type { Pill } from "../types";

export type OutlookSurface = "read" | "compose";

export type OutlookPillId =
  | "draft_reply"
  | "reply_all"
  | "new_email"
  | "create_draft"
  | "fill_compose"
  | "copy"
  | "book_meeting";

/**
 * Whether a pill click failed because it targeted something no longer open.
 *
 * Pills stay in the transcript forever, so one from an earlier turn can be
 * clicked after the user has switched email or moved to a compose window. The
 * Office.js handlers refuse in that case rather than writing into the wrong
 * thread — a correct refusal, but the raw message is developer-facing. Callers
 * use this to show a toast that says what to do instead.
 */
export function isStaleTargetError(error: unknown): boolean {
  if (typeof error !== "string") return false;
  const e = error.toLowerCase();
  return e.includes("no open email matches") || e.includes("is not available in this host");
}

/**
 * Bind every reply pill in `pills` to the conversation its turn was about.
 *
 * `compose.reply` falls back to Office.js `displayReplyForm`, which can only
 * reply to whatever is open at click time. Without a target, a button from an
 * earlier turn clicked after the user switched to another thread wrote that
 * turn's text into it (AIVA-8588). With one, `composeReply` sees the open
 * conversation has changed and refuses — {@link isStaleTargetError} then explains.
 * Another message in the same thread still counts as the target; see
 * integrations/tools/outlook/replyTarget.ts.
 *
 * A pill that already names a target keeps it, and `conversationId === null`
 * (no read item when the turn started) leaves the pills untouched, so nothing
 * changes for turns that had nothing to bind to.
 */
export function bindReplyPillsToConversation(pills: Pill[], conversationId: string | null): Pill[] {
  if (!conversationId) return pills;
  return pills.map((p) =>
    p.type === "office_action" && p.handler === "compose.reply" && !p.args?.[REPLY_TARGET_CONVERSATION_ARG]
      ? { ...p, args: { ...p.args, [REPLY_TARGET_CONVERSATION_ARG]: conversationId } }
      : p,
  );
}

/**
 * Whether a tool result represents a FAILURE.
 *
 * Deliberately conservative — it only reports failure on an unambiguous signal,
 * because the consequence of a false positive is a user losing the buttons they
 * need. Anything it cannot classify counts as success.
 *
 * The backend has no single error envelope, so this matches the three shapes
 * actually seen on the wire:
 *   - `"Error: email search failed — (Forbidden) …"`   (plain prefix)
 *   - `{"ok": false, "error": "…"}`                    (draft tools)
 *   - `{"status": "draft_not_submitted", "error": "…"}` (pill preconditions)
 */
export function toolOutputIsFailure(output: unknown): boolean {
  if (typeof output !== "string") return false;
  const trimmed = output.trim();
  if (/^error\b/i.test(trimmed)) return true;
  if (!trimmed.startsWith("{")) return false;
  try {
    const parsed = JSON.parse(trimmed) as Record<string, unknown>;
    if (parsed.ok === false) return true;
    return typeof parsed.error === "string" && parsed.error.length > 0;
  } catch {
    return false;
  }
}

/** What the pill builders need from the finished turn. */
export interface OutlookPillContext {
  /** The assistant's reply as rendered in the chat bubble. */
  readonly answerText: string;
  /**
   * The draft captured from `submit_email_draft`'s `tool_end` this turn, if any.
   *
   * Preferred over `answerText` for every draft-shaped button, because it is the
   * authoritative artefact: it carries the validated subject, to and cc that the
   * streamed text does not. Building a compose form from `answerText` alone would
   * open it with no recipients — a regression on what the backend-attached pill
   * does today.
   */
  readonly draft: EmailDraft | null;
}

/**
 * Skills whose deliverable is a DRAFT, mapped to the tool that produces it.
 *
 * For these the streamed text is a covering note ("Done — drafted a reply"), not
 * the artefact. So the question "is there anything to put on a button" is really
 * "did the draft tool succeed". A skill absent from this map is text-producing:
 * its answer IS the deliverable, so there is nothing to verify.
 *
 * ## Why this lives in the add-in and not the backend
 *
 * It was on the backend first, and it was wrong there. A HITL turn spans several
 * HTTP requests — approval pause, then a frontend-tool pause, then the final
 * stream — and the backend builds a fresh `TurnSink` per request. The draft tool
 * succeeds in an earlier request than the one that emits `done`, so by then the
 * backend has no record of it and reported every approved draft as producing
 * nothing.
 *
 * The add-in keeps ONE `TurnState` across the whole resume loop, so it is the
 * only layer that can see the draft and the `done` event together.
 *
 * Mirrors DRAFT_PRODUCING_SKILLS in shared/services/turn_committable.py, which
 * keeps the same map for the backend's own (single-request) decisions — keep the
 * two in step.
 */
export const DRAFT_PRODUCING_SKILLS: Readonly<Record<string, readonly string[]>> = {
  email_draft: ["submit_email_draft"],
  email_compose: ["submit_email_draft"],
  // Three tools, because a tone change must preserve an attachment (AIVA-8174).
  // A draft made by an attach skill is re-emitted through create_reply_draft /
  // create_compose_draft — the only tools that can carry an uploaded file — and only
  // a plain draft goes back through submit_email_draft.
  //
  // Listing just submit_email_draft made a SUCCESSFUL attachment-preserving retone
  // look like it produced nothing, so the whole action bar was suppressed and the
  // user got a single Reply pill. That breaks spec DR-04 AC-1: "the revised body
  // keeps the same facts and carries a full action bar".
  email_tone_adjuster: ["submit_email_draft", "create_reply_draft", "create_compose_draft"],
  send_attachment_draft: ["create_reply_draft"],
  send_attachment_compose: ["create_compose_draft"],
};

/**
 * Skills whose deliverable is a LIST of emails from the mailbox.
 *
 * When the list comes back EMPTY there is nothing to act on — the reply is
 * "No unread mail needs your attention right now", and offering *Draft reply*
 * under it invites the user to paste that sentence into an email.
 *
 * This is the deterministic replacement for the rejected `__NEED_INFO__` marker
 * (docs/outlook-action-pills-design.md, slice 4). It reads the TOOL's own result
 * count rather than asking the model to classify its reply, so it cannot be
 * forgotten and cannot misfire on a real answer.
 *
 * `email_search` is absent on purpose: it already renders no bar at all.
 */
const INBOX_LISTING_SKILLS: ReadonlySet<string> = new Set(["email_assistant"]);

interface OutlookPillSpec {
  readonly label: string;
  /**
   * The skill the PERSONA must have for this button to be offered, or null when
   * the button is never gated.
   *
   * Only buttons that LAUNCH ANOTHER SKILL are gated on that skill — today just
   * Book meeting, which starts the Calendar Assistant. The draft-shaped buttons
   * are gated on the skill whose capability they exercise, so a persona without
   * drafting rights does not get a one-click "Draft reply".
   *
   * `fill_compose` and `copy` map to no skill: they act on content the user can
   * already see in front of them and could place or copy by hand.
   */
  readonly gatedBy: string | null;
  /**
   * Whether this button puts an email BODY somewhere — into a reply window, a new
   * message, or the open compose form. Those buttons fall back to `answerText`
   * when no draft was captured, which is right for a text-producing skill (the
   * answer IS the content) but wrong for a draft-producing one, where the streamed
   * text is only a covering note. See the guard in {@link buildOutlookPills}.
   *
   * Copy is deliberately NOT marked: copying the covering note to the clipboard is
   * harmless and matches what the user can see. Book meeting carries no text.
   */
  readonly carriesEmailBody?: boolean;
  readonly build: (ctx: OutlookPillContext) => Pill | null;
}

/**
 * Render the model's plain-text reply as the HTML body of an email.
 *
 * Blank lines become paragraphs and single newlines become `<br>`, which is what
 * the specification's `aiTextToEmailHtml` describes for DR-01. Everything is
 * escaped first: this text ends up inside the user's outgoing email, so a stray
 * `<` or an `&` in the model's output must not become markup.
 *
 * Deliberately NOT the inverse of `htmlToClipboardText` in integrations/tools —
 * that one strips markup for the clipboard, which is a different job.
 */
export function textToEmailHtml(text: string): string {
  const escaped = escapeHtml(text);
  return escaped
    .split(/\n{2,}/)
    .map((para) => para.trim())
    .filter((para) => para.length > 0)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

/** The body an email-shaped pill should carry: the draft's if we have one. */
function emailBody(ctx: OutlookPillContext): string {
  return ctx.draft?.body ? ctx.draft.body : textToEmailHtml(ctx.answerText);
}

/**
 * Recipient/subject args, present only when a draft supplied them AND it is the
 * same kind of reply as the button.
 *
 * *forMode* is the mode the BUTTON means. A reply-all draft's recipient list is
 * the sender plus everyone on the original To and Cc lines; handing that to
 * "Draft reply" made the two buttons produce the identical draft, so a user who
 * deliberately chose the narrower action still wrote to the whole distribution
 * list. That is the disclosure DR-01/DR-03 keep separate, so the mismatch drops
 * the draft's recipients entirely and lets the handler build the correct set from
 * the open email (`displayReplyForm` vs `displayReplyAllForm` honour the mode).
 *
 * Omit *forMode* for buttons that are not reply-shaped — nothing to contradict.
 *
 * `draft_id` travels alongside subject/to/cc, not ahead of the *forMode* check:
 * on a mode mismatch the draft is the wrong KIND of reply for this button, so
 * its id must drop out with the rest rather than send the click down the wrong
 * Graph draft. When it does match, baking the id in here — this turn's own
 * `ctx.draft`, at build time — is the ONLY source of a button's draft id: a
 * click no longer borrows the session's `latestDraft`, which belongs to whatever
 * turn drafted last, not to THIS pill (AIVA-8588).
 */
function draftArgs(draft: EmailDraft | null, forMode?: "reply" | "reply_all"): Record<string, unknown> {
  if (!draft) return {};
  if (forMode && draft.mode && draft.mode !== forMode) return {};
  const args: Record<string, unknown> = {};
  if (draft.draft_id) args.draft_id = draft.draft_id;
  if (draft.subject) args.subject = draft.subject;
  if (draft.to?.length) args.to = draft.to;
  if (draft.cc?.length) args.cc = draft.cc;
  return args;
}

function officeAction(label: string, handler: string, args: Record<string, unknown>): Pill {
  return { type: "office_action", label, handler, args };
}

export const OUTLOOK_PILL_SPECS: Readonly<Record<OutlookPillId, OutlookPillSpec>> = {
  draft_reply: {
    label: "Draft reply",
    gatedBy: "email_draft",
    carriesEmailBody: true,
    build: (ctx) =>
      officeAction("Draft reply", "compose.reply", {
        body: emailBody(ctx),
        mode: "reply",
        ...draftArgs(ctx.draft, "reply"),
      }),
  },
  reply_all: {
    label: "Reply all",
    gatedBy: "email_draft",
    carriesEmailBody: true,
    build: (ctx) =>
      officeAction("Reply all", "compose.reply", {
        body: emailBody(ctx),
        mode: "reply_all",
        ...draftArgs(ctx.draft, "reply_all"),
      }),
  },
  new_email: {
    label: "New email",
    gatedBy: "email_compose",
    carriesEmailBody: true,
    // Deliberately NO `draftArgs`. This button sits on the REPLY bar, where the
    // captured draft is a reply — inheriting its subject ("Re: …") and its single
    // recipient would produce a reply, which is what `draft_reply` is for. The
    // specification's meaning here is "same content, someone else": carry the
    // body, let the user choose who it goes to.
    build: (ctx) =>
      officeAction("New email", "compose.open", {
        body: emailBody(ctx),
        body_format: "html",
      }),
  },
  create_draft: {
    // Same action as New email. The specification uses a different label when the
    // target is someone other than the open sender (§5, the New-email bar shape).
    label: "Create draft",
    gatedBy: "email_compose",
    carriesEmailBody: true,
    build: (ctx) =>
      officeAction("Create draft", "compose.open", {
        body: emailBody(ctx),
        body_format: "html",
        ...draftArgs(ctx.draft),
      }),
  },
  fill_compose: {
    label: "Fill compose",
    gatedBy: null,
    carriesEmailBody: true,
    // `text` is all the fast path itself uses — DR-01 is explicit that it leaves
    // subject and recipients alone, because Outlook has already set them on the
    // open window. The rest is carried for the handler's Graph fall-through
    // ("on failure it falls through to the Graph path"), which needs somewhere
    // to put a draft when there turns out to be no compose window to fill.
    build: (ctx) =>
      officeAction("Fill compose", "compose.insert", {
        text: emailBody(ctx),
        format: "html",
        ...draftArgs(ctx.draft),
      }),
  },
  copy: {
    label: "Copy",
    gatedBy: null,
    // ST-08 wants "the raw response text, not the draft-sanitised variant", so
    // this passes the text through unchanged. `clipboard.copy` is the single
    // point where anything reaches the clipboard and strips markup there if the
    // body turns out to be HTML, so a draft body is handled correctly too.
    build: (ctx) => officeAction("Copy", "clipboard.copy", { text: ctx.draft?.body || ctx.answerText }),
  },
  book_meeting: {
    label: "Book meeting",
    gatedBy: "calendar_assistant",
    // The only button that starts a new chat turn rather than calling Office.js.
    // `forcedSkillKey` makes the destination deterministic: the backend honours a
    // forced skill AHEAD of the router, so the click cannot be mis-routed by the
    // router reading the message differently. It still fails safe — the backend
    // refuses a forced skill the persona does not have.
    build: () => ({
      type: "chat_intent",
      label: "Book meeting",
      message: "Set up a meeting",
      forcedSkillKey: "calendar_assistant",
    }),
  },
};

const REPLY_BAR: readonly OutlookPillId[] = ["draft_reply", "reply_all", "new_email", "book_meeting", "copy"];
const NEW_EMAIL_BAR: readonly OutlookPillId[] = ["create_draft", "book_meeting", "copy"];
const COMPOSE_BAR: readonly OutlookPillId[] = ["fill_compose", "book_meeting", "copy"];
/**
 * The specification's fourth shape: "Meeting intent — prompt or reply is about
 * booking → Book meeting · Copy only (all email-draft buttons hidden)" (§5).
 *
 * The email-draft buttons are hidden because §4.1 rule 2 says a booking reply is
 * one sentence confirming what will be booked, NOT an email about the meeting —
 * so there is no body worth placing in a draft.
 */
const MEETING_BAR: readonly OutlookPillId[] = ["book_meeting", "copy"];
/** Search renders clickable result cards instead of a pill row (MS-01). */
const NO_BAR: readonly OutlookPillId[] = [];

/**
 * The four bar shapes, keyed by the intent read from the user's PROMPT.
 *
 * AIVA-7026 / spec §5 + §9: "The action bar reshapes for reply / new-email /
 * compose / meeting intent … Intent is read from the user's prompt, not the
 * AI's reply, because the prompt is the stronger evidence." This is what lets a
 * CM-02 agenda reply ("draft an agenda") carry the action bar so it can be
 * emailed in one click, while a booking ("set up a meeting") — the same
 * `calendar_assistant` skill — gets Book meeting · Copy. The skill alone cannot
 * tell those two apart (design doc §12); the prompt can.
 */
export type OutlookIntent = "reply" | "new_email" | "compose" | "meeting";

const BAR_FOR_INTENT: Readonly<Record<OutlookIntent, readonly OutlookPillId[]>> = {
  reply: REPLY_BAR,
  new_email: NEW_EMAIL_BAR,
  compose: COMPOSE_BAR,
  meeting: MEETING_BAR,
};

// Intent signals are split across small, word-boundaried regexes (rather than
// one large one) so each stays well under Sonar's regex-complexity limit (S5843)
// AND matches whole words — a raw substring test fired "minute" inside
// "last-minute"/"30 minutes" and "note" inside "denote". `t` is always
// lower-cased before these are checked.

// An EXPLICIT booking verb — "book", "schedule", "set up", "arrange". Wins
// outright: "book a last-minute meeting" / "schedule 30 minutes with Sam" are
// bookings, whatever agenda-ish or duration words trail behind.
const STRONG_BOOKING_RE = /\b(book|schedul|arrang)\w*|\bset[ -]?up\b/;

// A weaker booking hint — a bare slot pick, availability, "when is everyone
// free", "find me a time". Only counts when the prompt is NOT an agenda write-up.
const WEAK_BOOKING_RE =
  /\bslots?\b|\bavailab\w*|\b(everyone|anyone|you|we|they)\s+free\b|\bfind\b[\w\s]{0,15}\b(time|slot)\b/;

// Writing up a meeting (agenda / minutes / notes / recap / summary) is something
// you SEND — a reply, not a booking. Vetoes the WEAK booking / calendar-skill
// signal below, but never the strong verb above.
const AGENDA_RE = /\bagenda\b|\bsummar\w*|\brecap\b|\bnotes?\b|\bminutes\b|\bwrite[ -]?up\b/;

// A reply verb keeps the reply bar even when an address is present — in
// "reply to john@x.com" the address is who you're replying to, not a new
// recipient, so it must not fall through to the New-email shape.
const REPLY_VERB_RE = /\brepl(y|ies|ied)\b|\brespond\b/;

// Writing to someone other than the open sender → the New-email shape.
const NEW_EMAIL_PHRASES = [
  "new email",
  "send this to",
  "send it to",
  "send a note to",
  "send a copy to",
  "email this to",
  "email it to",
  "email to",
  "write to",
] as const;
const COMPOSE_OR_WRITE_EMAIL_RE = /\b(compose|write)\b.*\bemail\b/;
const EXPLICIT_ADDRESS_RE = /\bto\s+\S+@\S+/;

function includesAny(text: string, needles: readonly string[]): boolean {
  return needles.some((n) => text.includes(n));
}

/**
 * Read the reply / new-email / compose / meeting intent from what the user
 * typed. `surface === "compose"` always wins (a compose window is open, so the
 * only useful buttons act on it) — design doc §4 "Compose … this always wins".
 *
 * `skillKey` is a secondary signal used only for the meeting case: a plain slot
 * pick ("find me a time") routes to `calendar_assistant` but its prompt may not
 * contain a booking word, so a calendar turn that is NOT an agenda write-up is
 * treated as meeting intent.
 *
 * Exported for unit testing — this is the heart of the intent-driven bar.
 */
export function classifyOutlookIntent(
  promptText: string,
  skillKey: string,
  surface: OutlookSurface,
): OutlookIntent {
  if (surface === "compose") return "compose";
  const t = promptText.trim().toLowerCase();
  // An explicit booking verb wins over everything, so agenda/duration words in
  // the same phrase can't drag a booking into the reply bar.
  if (STRONG_BOOKING_RE.test(t)) return "meeting";
  const writingAgenda = AGENDA_RE.test(t);
  if (!writingAgenda && (WEAK_BOOKING_RE.test(t) || skillKey === "calendar_assistant")) return "meeting";
  if (
    !REPLY_VERB_RE.test(t) &&
    (includesAny(t, NEW_EMAIL_PHRASES) || COMPOSE_OR_WRITE_EMAIL_RE.test(t) || EXPLICIT_ADDRESS_RE.test(t))
  ) {
    return "new_email";
  }
  return "reply";
}

/**
 * Which buttons apply, per skill, per surface. Mirrors the agreed matrix in
 * docs/AiVA-Outlook-Action-Pills-Matrix.xlsx — keep the two in step.
 *
 * A skill that is absent from this table gets NO buttons. That is deliberate: a
 * newly seeded skill should render nothing rather than inherit a guessed bar.
 * `tests/chat/outlookPills.test.ts` fails when a seeded Outlook skill has no row.
 */
export const OUTLOOK_BARS: Readonly<Record<string, Readonly<Record<OutlookSurface, readonly OutlookPillId[]>>>> = {
  email_draft: { read: REPLY_BAR, compose: COMPOSE_BAR },
  email_compose: { read: NEW_EMAIL_BAR, compose: COMPOSE_BAR },
  email_tone_adjuster: { read: REPLY_BAR, compose: COMPOSE_BAR },
  summarize_assistant: { read: REPLY_BAR, compose: COMPOSE_BAR },
  // MS-03 / TF-01 / TF-02. Text-producing, and the specification asks for the bar
  // by name: TF-01 AC-3 — "the result carries the action bar so it can be sent as
  // a summary reply". Same shape as summarise, which is the same kind of turn.
  action_items_assistant: { read: REPLY_BAR, compose: COMPOSE_BAR },
  email_assistant: { read: REPLY_BAR, compose: COMPOSE_BAR },
  // No bar. This skill answers capability questions ("what can you do"), greetings,
  // and "that skill isn't enabled for you" refusals — none of which is an outcome
  // you would commit to an email or a meeting.
  //
  // Observed live before this changed: `what can you do?` rendered the full
  // REPLY_BAR, so pressing Draft reply offered to email the slash-command listing to
  // the sender. `hi` did the same with the greeting — Outlook never reaches
  // `small_talk`, which is scoped to Word, so general_outlook covers small talk too.
  // Which is the consistency argument: `small_talk` is already NO_BAR here, and so
  // are `email_search` and `nba_assistant`.
  //
  // Standing on the §10c ruling rather than on a citation: the specification does
  // NOT specify this case. §4 says a conversational fall-through "renders the email
  // action bar" and §9 gives four shapes (reply / new-email / compose / meeting) —
  // none of which is "informational". §4.1 rule 3 ("no forced email shape on an
  // informational answer") constrains the MODEL'S REPLY FORMAT, not the button row;
  // do not cite it for this. So this sits in the gap §10c reserves for "LLM replies
  // that are not really outcomes", alongside the A1 (all tools failed) and 3b (empty
  // inbox listing) suppressions in `nothingToActOn`.
  //
  // The one tension, accepted: ST-08 says "ANY AiVA response can be copied via the
  // action bar's Copy button", and this drops Copy. `small_talk`, `email_search` and
  // `nba_assistant` already make that trade.
  general_outlook: { read: NO_BAR, compose: NO_BAR },
  send_attachment_draft: { read: REPLY_BAR, compose: COMPOSE_BAR },
  send_attachment_compose: { read: NEW_EMAIL_BAR, compose: COMPOSE_BAR },
  // Search renders clickable result cards instead of a pill row (MS-01).
  email_search: { read: NO_BAR, compose: NO_BAR },
  // The specification's Meeting-intent shape (§5). Previously NO_BAR here, on the
  // grounds that the mockup never produces this shape and the slot picker is the
  // real affordance — but the specification is the source of truth where it
  // speaks, and it specifies Book meeting · Copy.
  //
  // Copy earns its place on every calendar turn (the slot list, the agenda, a
  // read-out of the day). Book meeting is redundant on a turn that already
  // rendered the slot picker, but it is what CM-02's agenda turns and plain
  // calendar reads need, and those share this skill key — so there is no way to
  // tell them apart from the skill alone.
  calendar_assistant: { read: MEETING_BAR, compose: COMPOSE_BAR },
  // Small talk is not a capability and produces nothing placeable.
  small_talk: { read: NO_BAR, compose: NO_BAR },
  // The next two are seeded for Outlook but appear in NEITHER the specification
  // nor the mockup, so there is no agreed bar for them. Listed explicitly with an
  // empty bar rather than omitted, because an absent row is indistinguishable
  // from someone forgetting one — and the seed-drift test would flag it as such.
  //
  // O2 in the matrix. Next Best Action's home in the specification is a separate
  // "Actions" tab rather than the chat pill row, so no buttons may well be the
  // right long-term answer for it; Summarize Uploaded File probably wants to
  // mirror Attachment Summarizer. Both need a business decision first.
  nba_assistant: { read: NO_BAR, compose: NO_BAR },
  summarize_uploaded_file: { read: NO_BAR, compose: NO_BAR },
};

/**
 * The persona's skill keys, or null when we do not know them yet.
 *
 * Read from the slash-command registry's in-memory cache, which is populated
 * from `GET /skills/slash-commands/{channel}` — the same persona-scoped list the
 * slash menu and the `/?` listing use, so the three can never disagree.
 *
 * Returns null (not an empty set) when the cache is cold, so callers can tell
 * "this persona has no skills" from "we have not been told yet". Two caveats
 * worth knowing:
 *
 *  - The endpoint only returns skills that HAVE a slash command. Every skill we
 *    gate on today has one (`/reply`, `/compose`, `/meeting`), so this works —
 *    but gating on a slash-less skill would silently fail open.
 *  - A cold cache fails OPEN (all buttons shown), matching how Word's
 *    `isAuthorised` treats an unknown `allowedTools` as "we don't know, allow".
 *    Failing closed would hide buttons on the first turn after a pane open.
 */
export function resolvePersonaSkillKeys(host: Channel | null): ReadonlySet<string> | null {
  const commands = getSkillCommandsForHost(host);
  if (commands.length === 0) return null;
  return new Set(commands.map((c) => c.skillKey).filter((k): k is string => !!k));
}

/**
 * The action pills for a finished Outlook turn.
 *
 * Returns `[]` — meaning "render no buttons" — when the skill or surface is
 * unknown, when the skill has no row in {@link OUTLOOK_BARS}, or when the turn
 * produced nothing placeable. Never guesses.
 */
export function buildOutlookPills(args: {
  readonly skillKey: string | undefined;
  readonly surface: OutlookSurface | null;
  readonly answerText: string;
  readonly draft: EmailDraft | null;
  readonly personaSkillKeys: ReadonlySet<string> | null;
  /**
   * The user's prompt for this turn (AIVA-7026 / spec §5, §9). When present it is
   * the PRIMARY signal for the bar shape — the bar reshapes for reply / new-email
   * / compose / meeting intent read from what the user typed, because the prompt
   * is stronger evidence than which skill ran. Absent on resume / rehydration
   * turns, where the skill's own bar (below) is used as the fallback.
   */
  readonly promptText?: string;
  /**
   * Whether the turn produced something that could be placed in an email. False
   * when a backend turn-level precondition refused — e.g. a reply skill invoked
   * from a compose window with no email to reply to. Defaults true so an older
   * backend that does not send it behaves as it does today (buttons shown).
   */
  readonly committable?: boolean;
  /**
   * Tools that ran AND succeeded at any point in this turn, INCLUDING earlier
   * resume streams. Used to confirm a draft-producing skill actually produced
   * one — see {@link DRAFT_PRODUCING_SKILLS}. An empty set means "nothing
   * succeeded yet", which for those skills means no buttons.
   */
  readonly succeededTools: ReadonlySet<string>;
  /**
   * Tools that ran this turn and FAILED. When tools ran and none succeeded, the
   * reply is an apology rather than an answer — see the check below.
   */
  readonly failedTools?: ReadonlySet<string>;
  /**
   * How many emails the turn's mailbox listing returned, or `null` if it never
   * ran. Zero means the skill looked and found nothing — see
   * {@link INBOX_LISTING_SKILLS}. `null` is NOT treated as empty.
   */
  readonly listEmailsResultCount?: number | null;
}): Pill[] {
  const {
    skillKey,
    surface,
    promptText,
    answerText,
    draft,
    personaSkillKeys,
    succeededTools,
    failedTools,
    listEmailsResultCount = null,
    committable = true,
  } = args;
  if (!skillKey || !surface || !committable) return [];

  // The skill's own bar still decides the AFFORDANCE: an unknown skill or a
  // NO_BAR skill (search → result cards, small talk → nothing) renders no button
  // row whatever the prompt says. These are not the skill-keyed special cases the
  // spec deletes — they route to a different UI, not a different bar SHAPE.
  const skillBar = OUTLOOK_BARS[skillKey]?.[surface];
  if (!skillBar || skillBar.length === 0) return [];
  if (nothingToActOn({ skillKey, answerText, draft, succeededTools, failedTools, listEmailsResultCount })) {
    return [];
  }

  // AIVA-7026 / spec §5, §9: shape the bar from the user's PROMPT when we have it
  // (reply / new-email / compose / meeting), because the prompt is the stronger
  // evidence — this is what gives a CM-02 agenda reply the action bar while a
  // booking on the same skill gets Book meeting · Copy. Fall back to the skill's
  // own bar on prompt-less turns (approval resume, rehydration).
  //
  // A NO_BAR skill never reaches here: the `skillBar.length === 0` guard above
  // returns early, so the prompt's intent cannot resurrect a bar for a skill that
  // has none. That is what keeps `general_outlook` bar-less however the capability
  // question happens to be phrased.
  const bar = promptText?.trim()
    ? BAR_FOR_INTENT[classifyOutlookIntent(promptText, skillKey, surface)]
    : skillBar;

  return pillsForBar(bar, { answerText, draft }, {
    // The skill produced a draft, but the add-in never saw its BODY. The
    // attachment tools are the case: `create_reply_draft` / `create_compose_draft`
    // return only `{draft_id, attachments, threaded}`, so `capturedDraft` stays
    // null while the draft-producing check above still passes on the draft_id.
    //
    // Body-carrying buttons must not fall back to `answerText` here, because for a
    // draft-producing skill that text is the covering note — "Done, I've drafted a
    // reply with the attachment included" — and a click would open a compose window
    // containing exactly that sentence instead of the draft.
    //
    // Dropping them loses nothing: the draft already exists in the mailbox, and
    // `handleCreateDraftToolEnd` emits an Open-draft pill from the tool's own
    // draft_id (AIVA-5594), which opens the real thing rather than re-inserting a
    // body. Book meeting and Copy still apply and are unaffected.
    draftBodyMissing: !!DRAFT_PRODUCING_SKILLS[skillKey] && !draft?.body,
    personaSkillKeys,
  });
}

/**
 * Whether the turn produced nothing worth offering buttons for.
 *
 * Split out of {@link buildOutlookPills} to keep both functions under Sonar's
 * cognitive-complexity threshold (S3776) — the same reason `_event_frames` is
 * split on the backend. It also gives the four "show nothing" rules one place to
 * live, which is where a reader looks when a bar unexpectedly does not render.
 *
 * These rules cover the cases the Outlook specification does not speak to —
 * apologies, clarifying questions, an empty mailbox listing. Where the
 * specification DOES name a bar, it wins: see §10c of the design doc.
 */
function nothingToActOn(args: {
  readonly skillKey: string;
  readonly answerText: string;
  readonly draft: EmailDraft | null;
  readonly succeededTools: ReadonlySet<string>;
  readonly failedTools?: ReadonlySet<string>;
  readonly listEmailsResultCount: number | null;
}): boolean {
  const { skillKey, answerText, draft, succeededTools, failedTools, listEmailsResultCount } = args;

  // Every tool this turn failed, so the reply is an error message — "I can't
  // access your inbox right now, please re-authenticate". Offering Draft reply
  // there invites the user to paste that apology into an email.
  //
  // Requires that tools ran AND none succeeded, so a turn that used no tools at
  // all (most summarise/general answers) is unaffected.
  if (failedTools && failedTools.size > 0 && succeededTools.size === 0) return true;

  // A draft-producing skill that never got a successful draft tool produced a
  // covering note or a question, not an artefact — so there is nothing for
  // Draft reply or Fill compose to place. This is the check that stops us pasting
  // "who should this go to?" into the user's own email.
  const required = DRAFT_PRODUCING_SKILLS[skillKey];
  if (required && !required.some((tool) => succeededTools.has(tool))) return true;

  // Looked in the mailbox and found nothing. Strictly `=== 0`: `null` means the
  // listing never ran, which says nothing either way and must not suppress.
  if (INBOX_LISTING_SKILLS.has(skillKey) && listEmailsResultCount === 0) return true;

  // Nothing to put on a button. A turn can end with pills already attached and
  // no streamed text (a paused tool call), so this is a real case, not a guard
  // against the impossible.
  return !(draft?.body || answerText.trim());
}

/** Build one bar's buttons, dropping the ones this turn or this persona cannot use. */
function pillsForBar(
  bar: readonly OutlookPillId[],
  ctx: OutlookPillContext,
  opts: { readonly draftBodyMissing: boolean; readonly personaSkillKeys: ReadonlySet<string> | null },
): Pill[] {
  const pills: Pill[] = [];
  for (const id of bar) {
    const spec = OUTLOOK_PILL_SPECS[id];
    if (spec.carriesEmailBody && opts.draftBodyMissing) continue;
    // A cold persona cache (null) fails open — see resolvePersonaSkillKeys.
    if (spec.gatedBy && opts.personaSkillKeys && !opts.personaSkillKeys.has(spec.gatedBy)) continue;
    const pill = spec.build(ctx);
    if (pill) pills.push(pill);
  }
  return pills;
}
